import fetchClient from './fetchClient';
import { errorHandler } from './utils';


const config = () => {
    return {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    };
};

export const getProducts = async (page: number = 1, limit: number = 100, customerId: string = null) => {
    const res: any = await fetchClient
        .get(
            `/products${
                customerId
                    ? `?customerId=${customerId}&page=${page}&limit=${limit}`
                    : `?page=${page}&limit=${limit}`
            }`,
            config(),
        )
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getProductsPurchasePrice = async (products: string[] = null) => {
    const res: any = await fetchClient
        .get(
            `/products/populated?limit=5000&productIds=${products.join(',')}`,
            config(),
        )
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getProductsAlreadyOrdered = async (customerId: string = null) => {
    const res: any = await fetchClient
        .get(`/products/already-ordered?customerId=${customerId}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getProductsVisibility = async (customerId: string = null, page: number = 1, limit: number = 100) => {
    const res: any = await fetchClient
        .get(`/products/visibility/${customerId}?page=${page}&limit=${limit}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getProduct = async (id: string) => {
    const res = await fetchClient.get(`/products/${id}`, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const updateProduct = async (product: any) => {
    const id = product._id;
    delete product._id;
    const res: any = await fetchClient
        .put(`/products/${id}`, product, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductCategory = async (product: any) => {
    const id = product._id;
    delete product._id;
    const res: any = await fetchClient
        .put(`/products/${id}/category`, product, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductVisibility = async (
    productId: string,
    customerId: string,
) => {
    const res: any = await fetchClient
        .put(`/products/visibility/${productId}/${customerId}`, {}, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductVisibilityBulk = async (
    productsIds: string[],
    customersIds: string[],
    newState: string,
) => {
    const res: any = await fetchClient
        .put(
            `/products/visibility-bulk`,
            {
                productsIds,
                customersIds,
                newState,
            },
            config(),
        )
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const duplicateProduct = async (productId: any) => {
    const res: any = await fetchClient
        .get(`/products/duplicate/${productId}`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const addProductImage = async (id: string, image: any) => {
    const res: any = await fetchClient
        .post(`/products/${id}/image`, image, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductImageOrder = async (productId: string, data: { imageUrls: string[] }) => {
    const res: any = await fetchClient
        .put(`/products/${productId}/images/reorder`, data, config())
        .catch(errorHandler);
    if (res?.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const deleteProduct = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/products/${id}`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const archiveProduct = async (id: string) => {
    const res: any = await fetchClient
        .patch(`/products/${id}/archive`, {}, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const unarchiveProduct = async (id: string) => {
    const res: any = await fetchClient
        .patch(`/products/${id}/unarchive`, {}, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const getArchivedProducts = async (): Promise<{ data: any; error: any }> => {
    const res: any = await fetchClient
        .get('/products/archived', config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const deleteProductImage = async (productId: string, data: { imageUrl: string, index: number }) => {
    const res: any = await fetchClient
        .delete(`/products/${productId}/image`, { 
            data,
            ...config() 
        })
        .catch(errorHandler);
    if (res?.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const getRecipe = async (productId: string) => {
    const res: any = await fetchClient
        .get(`/products/ingredients/${productId}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const createProduct = async (ingredient: any) => {
    const res = await fetchClient
        .post('/products', ingredient, config())
        .catch(errorHandler);
    return res.data;
};

export const getProductsByIds = async (ids: string[]) => {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    // Tentative endpoint bulk
    const bulk = await fetchClient
      .post('/products/bulk', { ids }, config())
      .catch(() => null);
    if (bulk?.data) return bulk.data;
  
    // Fallback séquentiel si pas d’endpoint bulk
    const results: any[] = [];
    await Promise.all(
      ids.map(async (id) => {
        const one = await fetchClient.get(`/products/${id}`, config()).catch(() => null);
        if (one?.data) results.push(one.data);
      }),
    );
    return results;
  };

  export const updateProductPrice = async (productId: any, newPrice: any) => {
    try {
      const res = await fetchClient.put(
        `/products/${productId}/update-price`,
        { purchasePrice: newPrice },
        config()
      );
      return res.data;
    } catch (err) {
      console.error('Erreur mise à jour du prix produit', err);
      return { error: true };
    }
  };

export const removeChildFromParent = async (parentId: string, childId: string) => {
   const res: any = await fetchClient
    .patch(`/products/${parentId}/remove-child/${childId}`, {}, config())
    .catch(errorHandler);
  if (!res) return null;
  return res.data;
};

export const getProductsBySubCategory = async (subCategoryName: string) => {
    const res: any = await fetchClient
        .get(`/products/by-subcategory/${encodeURIComponent(subCategoryName)}`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const resetProductsSubCategory = async (subCategoryName: string) => {
    const res: any = await fetchClient
        .put(`/products/reset-subcategory`, { subCategoryName }, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const getProductsByCategory = async (categoryId: string) => {
    const res: any = await fetchClient
        .get(`/products/by-category/${encodeURIComponent(categoryId)}`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const resetProductsCategory = async (categoryId: string) => {
    const res: any = await fetchClient
        .put(`/products/reset-category`, { categoryId }, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const downloadAllProducts = async () => {
    try {
        const res = await fetchClient.get('/products/export/csv', {
            ...config(),
            responseType: 'blob',
        });

        if (!res || !res.data) return null;

        const cd = (res.headers?.['content-disposition'] || '') as string;
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd || '');
        const filename = decodeURIComponent(match?.[1] || match?.[2] || 'produits.csv');

        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return true;
    } catch (err: any) {
        try {
            const blob = err?.response?.data as Blob | undefined;
            const text = blob ? await blob.text() : '';
            if (text) {
                const json = JSON.parse(text);
                errorHandler({ response: { data: { error: json?.error || 'Export impossible' } } });
            } else {
                errorHandler(err);
            }
        } catch {
            errorHandler(err);
        }
        return null;
    }
};

export const getProductPriceHistory = async (productId: string) => {
    const res: any = await fetchClient
        .get(`/products/${productId}/price-history`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

